import { fmt, type FmtKind } from '../ds';

/**
 * The CPM-pacing chart, ported from dualChart() in
 * ops/report-nightly/pages/marketing.html.
 *
 * WHY NOT components/charts/LineChart: that one floors its y-axis at zero,
 * which is right for spend and wrong here. A CPM series that lives between
 * ₹176 and ₹221 drawn from zero is a flat line, and this card exists to show
 * how much the daily figure moves. This keeps the published page's geometry
 * exactly — one shared linear scale across BOTH series with 15% padding — which
 * is the entire argument of the card: drawn separately, a line moving ₹13 and a
 * line moving ₹4 auto-normalise and look equally volatile.
 *
 * Nulls are dropped from the polyline rather than breaking it, as the page does.
 */
export function DualChart({ dates, a, b, kind }: {
  dates: string[];
  /** Primary series — dashed, grey. */
  a: Array<number | null>;
  /** Overlay on the SAME scale — solid, ink. Omit when it cannot be measured. */
  b?: Array<number | null>;
  kind: FmtKind;
}) {
  const all = [...a, ...(b ?? [])].filter((v): v is number => v !== null && Number.isFinite(v));
  if (all.length < 2) return null;
  const mn = Math.min(...all);
  const mx = Math.max(...all);
  const pad = (mx - mn) * 0.15 || 1;
  const lo = mn - pad;
  const hi = mx + pad;
  const W = 560, H = 120, padL = 54, padR = 10, padT = 10, padB = 20;
  const x = (i: number): number => padL + (i / (dates.length - 1 || 1)) * (W - padL - padR);
  const y = (v: number): number => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const line = (arr: Array<number | null>): string => arr
    .map((v, i) => (v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`))
    .filter((p): p is string => p !== null)
    .join(' ');
  const every = Math.max(1, Math.ceil(dates.length / 6));

  return (
    // "auto" is a CSS keyword, not an SVG length: as an ATTRIBUTE the browser
    // rejects it outright ("<svg> attribute height: Expected length") and falls
    // back to the 150px default, so the chart was drawn at a height it was not
    // laid out for. viewBox + width:100% + CSS height:auto is the pairing that
    // actually scales it, and it is the same shape the report's own charts use.
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ display: 'block', height: 'auto' }}
      role="img"
      aria-label="CPM by day"
    >
      {[0, 0.5, 1].map((t) => {
        const v = lo + (hi - lo) * t;
        const yy = y(v);
        return (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke="var(--line)" />
            <text x={padL - 6} y={yy + 3.3} textAnchor="end" fontSize="9" fill="var(--ink-3)">{fmt(v, kind)}</text>
          </g>
        );
      })}
      {/* Anchor the end labels inward. The plot area ends at W - padR, so a
          centred last tick hangs ~15px past the viewBox and the final date was
          clipped mid-digit ("09-1"); the same applies to the first at padL. */}
      {dates.map((d, i) => (i % every ? null : (
        <text
          key={d}
          x={x(i).toFixed(1)}
          y={H - 4}
          textAnchor={i === 0 ? 'start' : i >= dates.length - 1 ? 'end' : 'middle'}
          fontSize="9"
          fill="var(--ink-3)"
        >
          {d.slice(5)}
        </text>
      )))}
      <polyline
        points={line(a)}
        fill="none"
        stroke="var(--ink-3)"
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeDasharray="3 2.5"
        opacity=".85"
      />
      {b ? (
        <polyline points={line(b)} fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      ) : null}
    </svg>
  );
}

/** Port of chartLegend(): a 20×8 line swatch per series, solid or dashed. */
export function ChartLegend({ items }: { items: Array<{ label: string; solid: boolean }> }) {
  return (
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8 }}>
      {items.map((i) => (
        <span
          key={i.label}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: 'var(--ink-2)' }}
        >
          <svg width="20" height="8" aria-hidden="true">
            <line
              x1="0" y1="4" x2="20" y2="4"
              stroke={i.solid ? 'var(--ink)' : 'var(--ink-3)'}
              strokeWidth={i.solid ? 2.2 : 1.4}
              {...(i.solid ? {} : { strokeDasharray: '3 2.5' })}
              strokeLinecap="round"
            />
          </svg>
          {i.label}
        </span>
      ))}
    </div>
  );
}
